import { useEffect, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { getPractitionerList, getPractitionerAuthorizedList, getSingleUser } from "../../api/Individual";
import { failed, success } from "../../common/Toastify";
import { webSocketEndpoint } from "../../../environment";
import { editQueueItem, getActiveQueue } from "../api/WaitingRoom";
import { Col, Row, Table } from "react-bootstrap";
import { InputLabel, Skeleton, TableCell, Tooltip } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  AddCircleOutline,
  InfoOutlined,
  VideocamOutlined,
  CallOutlined,
  EditOutlined,
  DescriptionOutlined,
  History,
  ContentCopyOutlined,
} from "@mui/icons-material";
import { Button } from "../../common/Button";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { FullPageSpinner } from "../../common/Spinner/FullPageSpinner";
import AddNoteModal from "./AddNoteModal";
import ViewNotesModal from "./ViewNotesModal";
import AssignPractitionerModal from "./AssignPractitionerModal";
import PriorityHistoryModal from "./PriorityHistoryModal";
import EditReasonForCallModal from "./EditReasonForCallModal";
import SearchBar from "./SearchBar";
import { ViewEncounterModal } from "./ViewEncounterModal";
import CompactMultiSelect from "./CompactMultiSelect";
import CompactSelect from "./CompactSelect";
import SelectFiled from "../../common/textfield/SelectFiled";
import { getServiceLinesByOrgId } from "../../api/ServiceLine";
import { useFormik } from "formik";
import moment from "moment";
import { ViewDate } from "../../../utils/DateSupport";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";
import { getAccessToken } from "../../../utils/authStorage";
import { AUTH_TOKEN_REFRESHED_EVENT } from "../../../lib/axios";
import "./QueueCompactOps.css";

function getQueueRecordServiceLineId(data) {
  return (
    data?.serviceLineId ||
    data?.serviceLine?.reference?.split("/")?.[1] ||
    null
  );
}

// Presentational only — buckets priority/wait values into a soft color band.
// Does not affect the underlying number/data used anywhere else.
function priorityBandClass(priority) {
  const p = Number(priority);
  if (Number.isNaN(p)) return "q-prio-neutral";
  if (p <= 2) return "q-prio-critical";
  if (p <= 4) return "q-prio-warning";
  if (p <= 6) return "q-prio-neutral";
  return "q-prio-success";
}

function waitBandClass(waitMinutes) {
  const m = Number(waitMinutes);
  if (Number.isNaN(m)) return "q-wait-ok";
  if (m < 15) return "q-wait-ok";
  if (m < 40) return "q-wait-mid";
  return "q-wait-long";
}

// Display-only formatting for the Avg Wait insight tile — "38m" / "1h 12m".
function formatMinutes(totalMinutes) {
  const m = Number(totalMinutes) || 0;
  const hours = Math.floor(m / 60);
  const minutes = m % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

// Same condition the Practitioner column already uses to decide whether to
// show a name or an "Assign" button — reused here so the insights bar's
// "Unassigned" count always agrees with what the table itself shows.
function isAssigned(data) {
  const lastEntry = data?.practitionerHistory?.[data?.practitionerHistory?.length - 1];
  return Boolean(lastEntry?.actor?.display && lastEntry.actor.display !== "-");
}

function Queue() {
  const practitionerId = useSelector(
    (state) => state?.auth?.user?.["custom:unique_id"]
  );
  const [myUserId, setMyUserId] = useState("");
  const practUserName = useSelector(
    (state) => state?.auth?.user?.name[0]?.text
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [userScopeOfPractice, setUserScopeOfPractice] = useState("");
  const [initiateWebsocketConnection, setInitiateWebsocketConnection] =
    useState(false);
  const [accessTokenVersion, setAccessTokenVersion] = useState(0);
  const userOrganizationId = useSelector(
    (state) => state?.auth?.user?.["organizations"][0].id
  );
  const [queuePatients, setQueuePatients] = useState([]);
  const [page, setPage] = useState(0);
  const ROWS_PER_PAGE_OPTIONS = [25, 50, 75, 100];
  const DEFAULT_ROWS_PER_PAGE = 50;
  const rowsPerPageStorageKey = practitionerId
    ? `queue.rowsPerPage.${practitionerId}`
    : null;
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const totalRows = queuePatients?.length;
  const [currentTime, setCurrentTime] = useState(new Date());
  const [addNoteModalShow, setAddNoteModalShow] = useState(false);
  const [viewNoteModalShow, setViewNoteModalShow] = useState(false);
  const [addNoteModalData, setAddNoteModalData] = useState(null);
  const [assignPractData, setAssignPractData] = useState(null);
  const [assignPractModalShow, setAssignPractModalShow] = useState(false);
  const [newRecordId, setNewRecordId] = useState(null);
  const [copiedEncounterId, setCopiedEncounterId] = useState(null);
  const [encounterModal, setEncounterModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [priorityHistoryModal, setPriorityHistoryModal] = useState(false);
  const [editReasonModalShow, setEditReasonModalShow] = useState(false);
  const [editReasonModalData, setEditReasonModalData] = useState(null);
  const [searchFilterData, setSearchFilterData] = useState([]);
  const [practitionersUnderOrg, setPractitionersUnderOrg] = useState([]);
  const [practitionerOptions, setPractitionerOptions] = useState([]);
  const [selectedPractitionerFilter, setSelectedPractitionerFilter] =
    useState(null);
  const [selectedStatus, setSelectedStatus] = useState({
    data: "arrived",
    value: "arrived",
    label: "Arrived",
  });
  const [orgData, setOrgdata] = useState({})
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null)
  const [availableServiceLines, setAvailableServiceLines] = useState([]);
  const serviceLineFilterRef = useRef("");
  const selectedPrioritiesRef = useRef([]);
  const [isCTIEnabled, setCTIEnabled] = useState(false)
  const [myUserData, setMyUserData] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!rowsPerPageStorageKey) return;
    try {
      const raw = localStorage.getItem(rowsPerPageStorageKey);
      const parsed = raw ? Number(raw) : NaN;
      if (ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        setRowsPerPage(parsed);
      } else {
        setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
      }
    } catch (e) {
      setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
    }
  }, [rowsPerPageStorageKey]);

  useEffect(() => {
    if (!rowsPerPageStorageKey) return;
    try {
      localStorage.setItem(rowsPerPageStorageKey, String(rowsPerPage));
    } catch (e) {
      // no-op (storage may be unavailable)
    }
  }, [rowsPerPageStorageKey, rowsPerPage]);

  useOrgQuery({
    onSuccess: (data) => {
      setOrgdata(data);
      setOrganizations(data?.map((org) => ({ label: org?.name, value: org?.id }))) 
      const myOrg = data?.find(
        (org) => org?.id === userOrganizationId
      );

      setCTIEnabled(myOrg?.ctiSettings?.enabled || false);
      //setCTIEnabled(data?.[0]?.ctiSettings?.enabled)
      if(userScopeOfPractice){
        fetchData(userScopeOfPractice);
      }
    },
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    const onTokenRefreshed = () => setAccessTokenVersion((v) => v + 1);
    window.addEventListener(AUTH_TOKEN_REFRESHED_EVENT, onTokenRefreshed);
    return () => window.removeEventListener(AUTH_TOKEN_REFRESHED_EVENT, onTokenRefreshed);
  }, []);
  
  useEffect(() => {
    const token = getAccessToken();
    if (initiateWebsocketConnection && token && userOrganizationId && userScopeOfPractice) {
      fetchData(userScopeOfPractice);
      let orgIdsList;
      if (selectedOrganization) {
        // If user explicitly selected an organization, send only that one (small enough for URL)
        orgIdsList = [selectedOrganization];
      } else {
        // Send null - backend will derive full list server-side from user's base orgId
        orgIdsList = null;
      }

      // Ensure scopeOfPractice is an array before stringifying (matching server expectation)
      const scopeOfPracticeArray = Array.isArray(userScopeOfPractice) ? userScopeOfPractice : [userScopeOfPractice];
      const encodedScopeOfPractice = encodeURIComponent(JSON.stringify(scopeOfPracticeArray));
      
      // Build WebSocket URL - only include orgIdsList if provided (for backward compatibility)
      // If null, backend will derive it server-side (same pattern as getQueue API)
      const orgIdsListParam = orgIdsList ? `&orgIdsList=${encodeURIComponent(JSON.stringify(orgIdsList))}` : '';
      const wsUrl = `wss://${webSocketEndpoint}?token=${token}&organizationId=${userOrganizationId}${orgIdsListParam}&scopeOfPractice=${encodedScopeOfPractice}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log("WebSocket connection opened successfully");
        //setLoading(false);
      };
      ws.onmessage = (event) => {
        const newRecord = JSON.parse(event.data);

        switch (newRecord.messageType) {
          case "INSERT":
            if (newRecord?.status === selectedStatus?.value) {
              const lineFilter = serviceLineFilterRef.current;
              if (
                lineFilter &&
                getQueueRecordServiceLineId(newRecord) !== lineFilter
              ) {
                break;
              }
              const selectedPriorities = selectedPrioritiesRef.current;
              if (
                selectedPriorities?.length > 0 &&
                !selectedPriorities.includes(newRecord.priority)
              ) {
                break;
              }
              setQueuePatients((prevTableData) => {
                // Check if record already exists to prevent duplicates
                const existingIndex = prevTableData.findIndex(
                  (item) => item?.id === newRecord?.id
                );
                
                // Only add if it doesn't already exist
                if (existingIndex === -1) {
                  const updatedTableData = [...prevTableData, newRecord];
                  sortByPriorityAndStartDate(updatedTableData);
                  setNewRecordId(newRecord.id);
                  setTimeout(() => {
                    setNewRecordId(null);
                  }, 500);

                  return updatedTableData;
                }
                
                // If it already exists, return the previous data unchanged
                return prevTableData;
              });
            }
            break;

          case "MODIFY":
            // If the modified record matches the current filter, update it
            if (newRecord?.status === selectedStatus?.value) {
              const lineFilter = serviceLineFilterRef.current;
              const matchesLine =
                !lineFilter ||
                getQueueRecordServiceLineId(newRecord) === lineFilter;
              const selectedPriorities = selectedPrioritiesRef.current;
              const matchesPriority =
                !selectedPriorities?.length ||
                selectedPriorities.includes(newRecord.priority);
              const matchesFilters = matchesLine && matchesPriority;
              setQueuePatients((prevTableData) => {
                const existingIndex = prevTableData.findIndex(
                  (item) => item?.id === newRecord?.id
                );
                if (!matchesFilters) {
                  if (existingIndex !== -1) {
                    const updatedTableData = [...prevTableData];
                    updatedTableData.splice(existingIndex, 1);
                    sortByPriorityAndStartDate(updatedTableData);
                    return updatedTableData;
                  }
                  return prevTableData;
                }
                if (existingIndex !== -1) {
                  const updatedTableData = [...prevTableData];
                  updatedTableData[existingIndex] = newRecord;
                  sortByPriorityAndStartDate(updatedTableData);
                  return updatedTableData;
                }
                return prevTableData;
              });
            } 
            // If the record no longer matches the filter, remove it
            else {
              setQueuePatients((prevTableData) => {
                const existingIndex = prevTableData.findIndex(
                  (item) => item?.id === newRecord?.id
                );

                if (existingIndex !== -1) {
                  const updatedTableData = [...prevTableData];
                  updatedTableData.splice(existingIndex, 1);
                  sortByPriorityAndStartDate(updatedTableData);
                  return updatedTableData;
                }
                return prevTableData;
              });
            }
            break;
          case "REMOVE":
            // Remove the record from the queue regardless of status
            setQueuePatients((prevTableData) => {
              const existingIndex = prevTableData.findIndex(
                (item) => item?.id === newRecord?.id
              );

              if (existingIndex !== -1) {
                const updatedTableData = [...prevTableData];
                updatedTableData.splice(existingIndex, 1);
                sortByPriorityAndStartDate(updatedTableData);
                return updatedTableData;
              }
              return prevTableData;
            });
            break;
          default:
            break;
        }
      };

      ws.onclose = (event) => {
        console.log("WebSocket connection closed:", event);
      };

      ws.onerror = (error) => {
        console.error("WebSocket encountered an error:", error);
      };

      return () => {
        if (ws) {
          ws.close();
        }
      };
    }
  }, [initiateWebsocketConnection, selectedStatus, selectedOrganization, accessTokenVersion, userOrganizationId, userScopeOfPractice]);

  useEffect(() => {
    getPractitionerAuthorizedList((selectedOrganization || userOrganizationId), null, null, true)
      .then((res) => {
        if (res.status === true) {
          setPractitionersUnderOrg(res?.result);
          setPractitionerOptions(res?.result?.map((practitioner) => ({
                value: practitioner.id,
                // label: capitalizeWords(practitioner.name?.[0]?.text),
                label: capitalizeWords(practitioner.name?.[0]?.text),
                data: practitioner,
              })).sort((a, b) =>  a.label.localeCompare(b.label) /*a.value.localeCompare(b.value)*/));
        }
      })
      .catch((res) => {
        failed(
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
        );
      });
  }, [selectedOrganization]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userRes = await getSingleUser({ id: practitionerId, type: "Practitioner" });
        const scope = userRes?.data?.scopeOfPractice;
  
        if (!scope) return; 
  
        setMyUserData(userRes?.data);
        setUserScopeOfPractice(scope);
        setMyUserId(userRes?.data?.link?.[0]?.target?.reference?.split("/")?.[1]);
        setInitiateWebsocketConnection(true);
  
      } catch (err) {
        failed(
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message
        );
      }
    };
  
    fetchUserData();
  }, []);

  const sortByPriorityAndStartDate = (data) => {
    if (!Array.isArray(data)) return [];

    return data.sort((a, b) => {
      const priorityComparison = a.priority - b.priority;
      if (priorityComparison === 0) {
        return new Date(a?.period?.start || 0) - new Date(b?.period?.start || 0);
      }
      return priorityComparison;
    });
  }
  
  const fetchData = async (scope) => {
    if (!scope) return;
  
    setIsLoading(true);
    try {
      const encodedScopeOfPractice = encodeURIComponent(JSON.stringify(scope)); 
  
      const res = await getActiveQueue({
        scopeOfPractice: encodedScopeOfPractice,
        status: selectedStatus?.value,
        orgId: userOrganizationId,
        // Pass selectedOrgId when user explicitly selects an org from dropdown
        ...(selectedOrganization && { selectedOrgId: selectedOrganization })
      });
  
      let sortedData = sortByPriorityAndStartDate(res?.result || []);
  
      if (formik?.values?.selectedPriorityLevels.length !== 0) {
        const selectedPriorities = formik.values.selectedPriorityLevels;
        sortedData = sortedData.filter((data) =>
          selectedPriorities.includes(data.priority)
        );
      }
  
      if (formik?.values?.selectedPractitionerFilter) {
        const selectedPractitioner = formik.values.selectedPractitionerFilter;
        sortedData = sortedData.filter(
          (data) =>
            data?.practitionerHistory &&
            data?.practitionerHistory[data?.practitionerHistory.length - 1]?.actor?.reference?.split("/")[1] === selectedPractitioner?.data?.id
        );
      }

      if (formik?.values?.serviceLineId) {
        const sid = formik.values.serviceLineId;
        sortedData = sortedData.filter(
          (data) => getQueueRecordServiceLineId(data) === sid
        );
      }

      setQueuePatients(sortedData);
      setSearchFilterData(sortedData);
    } catch (err) {
      failed(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message
      );
    } finally {
      setIsLoading(false);
    }
  };
  

  // Raw elapsed minutes since arrival — used anywhere the number itself is
  // needed (insight totals, wait-band coloring), as opposed to the
  // formatted "XhYm" text calculateWaitTime produces for display.
  const getWaitMinutes = (arrivalTimestamp) => {
    if (!arrivalTimestamp) return 0;
    return Math.max(0, Math.floor((Date.now() - arrivalTimestamp) / (1000 * 60)));
  };

  const calculateWaitTime = (arrivalTimestamp) => {
    const totalMinutes = getWaitMinutes(arrivalTimestamp);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h${minutes}m`;
  };

  // Derived purely from the already-loaded/filtered queue data — no new
  // fetch, no new websocket subscription. Recomputes whenever the visible
  // rows change (search/filters/websocket updates) or the minute-ticker
  // advances, so Avg/Longest Wait keep up as time passes.
  const queueInsights = useMemo(() => {
    const rows = searchFilterData || [];
    const total = rows.length;
    let unassigned = 0;
    let waitSum = 0;
    let longestWait = 0;
    // Same 1-8 priority scale used by the per-row editor (priorityOptions
    // below) and the enroll/edit forms — counts per exact number, not banded.
    const priorityCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0 };

    rows.forEach((item) => {
      if (!isAssigned(item)) unassigned += 1;

      const wait = getWaitMinutes(item?.createdAt);
      waitSum += wait;
      if (wait > longestWait) longestWait = wait;

      const p = Number(item?.priority);
      if (priorityCounts[p] !== undefined) priorityCounts[p] += 1;
    });

    const avgWaitMinutes = total > 0 ? Math.round(waitSum / total) : 0;

    return { total, unassigned, avgWaitMinutes, longestWait, priorityCounts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilterData, currentTime]);
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(intervalId);
  }, []);

  const handleUpdatePriority = (data, newPriority) => {
    setFullScreenLoader(true);
    const currentPriorityHistory = data?.priorityHistory || [];
    let actorPriorityChange = {
      actor: {
        display: practUserName,
        reference: `Practitioner/${myUserId}`,
        type: "Practitioner",
      },
      oldPriority: data?.priority,
      newPriority: newPriority,
      updatedAt: Date.now(),
    };
    const updatedPriorityHistory = [
      ...currentPriorityHistory,
      actorPriorityChange,
    ];
    editQueueItem(
      {
        id: data?.id,
        priority: newPriority,
        priorityHistory: updatedPriorityHistory,
      },
      userOrganizationId
    )
      .then((res) => {
        success(
          `Priority updated for ${capitalizeWords(
            data?.subject?.display
          )} to ${newPriority}`
        );
      })
      .catch((res) => {
        failed(
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
        );
      })
      .finally(() => setFullScreenLoader(false));
  };

  const handleAddNoteClick = (data) => {
    setAddNoteModalData(data);
    setAddNoteModalShow(true);
  };

  const handleViewNotesClick = (data) => {
    setAddNoteModalData(data);
    setViewNoteModalShow(true);
  };

  const handleViewEncounterClick = (data) => {
    setSelectedRecord(data);
    setEncounterModal(true);
  };

  const capitalizeWords = (str) => {
    return (str) ? str
      .toLowerCase()
      .replace(/(^|\s)\S/g, (firstLetter) => firstLetter.toUpperCase()) : "" ;
  };

  const handleAssignPractitionerClick = (data) => {
    setAssignPractData(data);
    setAssignPractModalShow(true);
  };

  const handlePriorityHistoryView = (data) => {
    setSelectedRecord(data);
    setPriorityHistoryModal(true);
  };

  const handlePatientView = (data) => {
    const baseUrl = window.location.origin;
    const patientIdView = data?.subject?.reference?.split("/")?.[1];
    const patientNameView = data?.subject?.display;
    const url = `${baseUrl}/app/patients-details?patientId=${patientIdView}`//&patientName=${patientNameView}`;
    window.open(url, "_blank");
  };

  const handleCopyEncounterId = async (id, e) => {
    e?.stopPropagation();
    if (!id) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(id);
      } else {
        // Fallback for browsers/contexts without the Clipboard API
        const textarea = document.createElement("textarea");
        textarea.value = id;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedEncounterId(id);
      setTimeout(() => {
        setCopiedEncounterId((current) => (current === id ? null : current));
      }, 1500);
    } catch (err) {
      failed("Couldn't copy Encounter ID");
    }
  };

  const handleFilterData = (data, resetPage = false) => {
    setSearchFilterData(data);
    if (resetPage) {
      setPage(0);
    }
  };

  // Keep current page when the list grows/shrinks; only move if it is out of range
  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(searchFilterData.length / rowsPerPage) - 1
    );
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [searchFilterData.length, rowsPerPage, page]);

  const statusOptions = [
    { status: "arrived", value: "arrived" },
    { status: "in-progress-waitingroom", value: "in-progress-waitingroom" },
    { status: "in-progress-appointment", value: "in-progress-appointment" },
    { status: "parked", value: "parked" },
    { status: "follow-up", value: "follow-up" }
  ].map((option) => ({
    value: option.value,
    label: capitalizeWords(option.status),
    data: option.status,
  }));

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    formik.setFieldValue('selectedPriorityLevels', []);
  };

  const priorityOptions = [
    [1, 2, 3, 4, 5, 6, 7, 8].map((option) => ({
      name: option,
      value: option,
      id: option,
    })),
  ];

  const formik = useFormik({
    initialValues: {
      selectedPriorityLevels: [],
      selectedPractitionerFilter: null,
      serviceLineId: "",
    },
  });

  const handleFiltersChange = (filterArray) => {
    fetchData(userScopeOfPractice);
    page !== 0 && setPage(0);
  };

  const handleOrgChange = (event) => {
    let value = event?.value;
    setSelectedOrganization(value);
    formik.setFieldValue("organization", value);
    formik.setFieldValue("practitionerId", "")
    formik.setFieldValue("serviceLineId", "");
    setPractitionersUnderOrg([]);
    setPractitionerOptions([]);
    setPage(0);
  };

  const serviceLineSelectOptions = availableServiceLines.map((sl) => ({
    label: sl.name,
    value: sl.id,
  }));

  const handleServiceLineChange = (selected) => {
    formik.setFieldValue("serviceLineId", selected?.value || "");
    setPage(0);
  };

  useEffect(() => {
    handleFiltersChange();
  }, [formik?.values?.selectedPriorityLevels, formik?.values?.selectedPractitionerFilter, formik?.values?.serviceLineId]);

  useEffect(() => {
    serviceLineFilterRef.current = formik.values.serviceLineId || "";
  }, [formik.values.serviceLineId]);

  useEffect(() => {
    selectedPrioritiesRef.current =
      formik.values.selectedPriorityLevels || [];
  }, [formik.values.selectedPriorityLevels]);

  useEffect(() => {
    const orgForServiceLines =
      organizations.length > 1 ? selectedOrganization : userOrganizationId;

    if (organizations.length > 1 && !selectedOrganization) {
      setAvailableServiceLines([]);
      formik.setFieldValue("serviceLineId", "");
      return;
    }

    if (!orgForServiceLines) {
      setAvailableServiceLines([]);
      return;
    }

    getServiceLinesByOrgId(orgForServiceLines)
      .then((res) => {
        if (res?.status === true && Array.isArray(res?.data)) {
          const formatted = res.data
            .map((item) => ({
              name: item?.name,
              id: item?.id,
              value: item?.id,
            }))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setAvailableServiceLines(formatted);
        } else {
          setAvailableServiceLines([]);
        }
      })
      .catch(() => {
        setAvailableServiceLines([]);
      });
  }, [selectedOrganization, organizations.length, userOrganizationId]);

  const handlePreviewEncounterClick = (data, from = "draft") => {
    const baseUrl = window.location.origin;
    const encounterId = data?.id;
    const patientId = data?.subject?.reference?.split("/")?.[1];
    const url = `${baseUrl}/app/encounter-report?encounterId=${encounterId}&from=${from}`;
    window.open(url, "_blank");
  };

  return (
    <>
      {fullScreenLoader && <FullPageSpinner loadingText="Loading..." />}
      <section className="common-listing">
      <div className="dashSearch queue-toolbar">
        <Row className="g-2 align-items-end">
          <Col xs="auto">
              <SearchBar
                list={queuePatients || []}
                filterData={handleFilterData}
                style={{ width: 320, boxSizing: "border-box" }}
              />
          </Col>
          <Col xs="auto">
          <InputLabel
                id="queue-status-label"
              >
                {"Status"}
              </InputLabel>
              <CompactSelect
                options={statusOptions}
                value={selectedStatus}
                onChange={handleStatusChange}
                isClearable={false}
                isSearchable={false}
                isLoading={statusOptions?.length > 0 ? false : true}
              />
          </Col>
          <Col xs="auto">
            <InputLabel
                id="queue-priority-label"
              >
                Priority
              </InputLabel>
            <CompactMultiSelect
                options={priorityOptions[0]}
                value={formik.values.selectedPriorityLevels}
                onChange={(values) => formik.setFieldValue("selectedPriorityLevels", values)}
                placeholder="Priority"
                nodata="No Data Found"
              />
          </Col>
          {organizations.length > 1 && (
            <Col xs="auto">
              <InputLabel
                id="queue-org-label"
              >
                Organization
              </InputLabel>
              <CompactSelect
                options={organizations}
                value={organizations.find((org) => org.value === selectedOrganization) || null}
                onChange={handleOrgChange}
                placeholder="Select Organization"
                isClearable
                isLoading={organizations?.length <= 1}
              />
            </Col>
          )}

          <Col xs="auto">
            <InputLabel
                id="queue-service-line-label"
              >
                Service Line
              </InputLabel>
            <CompactSelect
                options={serviceLineSelectOptions}
                value={
                  serviceLineSelectOptions.find(
                    (opt) => opt.value === formik.values.serviceLineId
                  ) || null
                }
                onChange={handleServiceLineChange}
                placeholder={
                  organizations.length > 1 && !selectedOrganization
                    ? "Select organization first"
                    : "Select Service Line"
                }
                isDisabled={organizations.length > 1 && !selectedOrganization}
                isClearable
              />
          </Col>
          <Col xs="auto">
            <InputLabel
              id="queue-practitioner-label"
            >
              Practitioner
            </InputLabel>
            <CompactSelect
              options={practitionerOptions}
              value={formik.values.selectedPractitionerFilter}
              onChange={(option) => formik.setFieldValue("selectedPractitionerFilter", option)}
              placeholder="Select Practitioner"
              isClearable
              isLoading={practitionerOptions?.length > 0 ? false : true}
            />
          </Col>
        </Row>
      </div>
      <div className="queue-insights-bar">
        <div className="queue-insight">
          <span className="queue-insight-value">{queueInsights.total}</span>
          <span className="queue-insight-label">In Queue</span>
        </div>
        <div className="queue-insight-divider" />
        <div className="queue-insight">
          <span className="queue-insight-value">{queueInsights.unassigned}</span>
          <span className="queue-insight-label">Unassigned</span>
        </div>
        <div className="queue-insight-divider" />
        <div className="queue-insight">
          <span className="queue-insight-value">{formatMinutes(queueInsights.avgWaitMinutes)}</span>
          <span className="queue-insight-label">Avg Wait</span>
        </div>
        <div className="queue-insight-divider" />
        <div className="queue-insight">
          <span className="queue-insight-value">{formatMinutes(queueInsights.longestWait)}</span>
          <span className="queue-insight-label">Longest Wait</span>
        </div>
        <div className="queue-insight-divider" />
        <div className="queue-insight queue-insight-priority-mix">
          <span className="queue-insight-label">Priority Mix</span>
          <span className="queue-insight-breakdown">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((p) => (
              <span
                key={p}
                className={`queue-priority-count ${priorityBandClass(p)}${queueInsights.priorityCounts[p] === 0 ? " is-zero" : ""}`}
              >
                P{p} {queueInsights.priorityCounts[p]}
              </span>
            ))}
          </span>
        </div>
      </div>
        <div className="queue-table-card">
          <div className="queue-table-scroll">
          <Table className="queue-compact-table">
            <thead>
              <tr>
                <th>Category</th> {/* Changed from Priority Reason to Category - Encouter Enhancement */}
                <th>Priority</th>
                <th style={{ textAlign: "left" }}>Patient Name</th>
                <th style={{ textAlign: "left" }}>Encounter ID</th>
                <th style={{ textAlign: "center"}}>Organization</th>
                <th style={{ textAlign: "center" }}>Location</th>
                <th style={{ textAlign: "center" }}>Reason</th>
                <th style={{ textAlign: "center" }}>Wait Time</th>
                <th style={{ textAlign: "center", minWidth: "56px" }}>
                  Notes
                </th>
                <th style={{ textAlign: "center" }}>Practitioner</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map((val) => (
                  <tr key={val}>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                    <td>
                      <Skeleton animation="wave" />
                    </td>
                  </tr>
                ))
              ) : searchFilterData?.length > 0 ? (
                searchFilterData
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  ?.map((data, index) => {
                    const waitMinutes = getWaitMinutes(data?.createdAt);
                    const waitTime = calculateWaitTime(data?.createdAt);
                    data.orgName = organizations.find(org => org.value === data.orgId)?.label
                    const rowClassName = [
                      newRecordId === data?.id ? "queue-row-new" : "",
                      index % 2 === 1 ? "queue-row-alt" : "",
                    ].filter(Boolean).join(" ") || undefined;

                    return (
                      <tr key={data?.id} className={rowClassName}>
                        <td
                          style={{
                            textTransform: "capitalize",
                          }}
                        >
                          {data?.intakeQuestions?.intakePriorityReason
                            ? data?.intakeQuestions?.intakePriorityReason
                            : "-"}
                        </td>
                        <td>
                          <div className="queue-priority-cell">
                            {selectedStatus.value === "arrived" ||
                            selectedStatus.value === "parked" ||
                            selectedStatus.value === "follow-up" ? (
                              <span className={`queue-priority-pill ${priorityBandClass(data?.priority)}`}>
                                <Select
                                  classNamePrefix="priority-select"
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  maxMenuHeight={320}
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                                    menu: (base) => ({ ...base, minWidth: 72, fontSize: 13 }),
                                    menuList: (base) => ({ ...base, padding: 4 }),
                                    option: (base, state) => ({
                                      ...base,
                                      padding: "6px 10px",
                                      borderRadius: 6,
                                      fontSize: 13,
                                      fontWeight: state.isSelected ? 700 : 500,
                                      backgroundColor: state.isFocused ? "#f4f7fb" : "#ffffff",
                                      color: "#16213b",
                                      cursor: "pointer",
                                    }),
                                  }}
                                  value={
                                    data?.priority !== null
                                      ? {
                                          label: data?.priority,
                                          value: data?.priority,
                                        }
                                      : data.priority
                                      ? {
                                          label: data.priority,
                                          value: data.priority,
                                        }
                                      : null
                                  }
                                  options={[1, 2, 3, 4, 5, 6, 7, 8].map((value) => ({
                                    label: value,
                                    value,
                                  }))}
                                  onChange={(selectedOption) => {
                                    handleUpdatePriority(
                                      data,
                                      selectedOption ? selectedOption.value : null
                                    );
                                  }}
                                />
                              </span>
                            ) : (
                              <span className={`queue-priority-badge ${priorityBandClass(data?.priority)}`}>
                                {data?.priority}
                              </span>
                            )}
                            <Tooltip
                              title={"View Priority History"}
                              style={{ marginLeft: "5px" }}
                            >
                              <div>
                                <Link
                                  variant="primary"
                                  className="view-btn queue-icon-link"
                                  onClick={() =>
                                    handlePriorityHistoryView(data)
                                  }
                                >
                                  <History style={{ color: "#005ba8" }} />
                                </Link>
                              </div>
                            </Tooltip>
                          </div>
                        </td>

                        <td
                          style={{
                            textAlign: "left",
                            textTransform: "capitalize",
                          }}
                        >
                          <span
                            style={{
                              color: "#005ba8",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() => handlePatientView(data)}
                          >
                            {data?.subject?.display}
                          </span>
                        </td>
                        <td style={{ textAlign: "left" }} className="queue-mono-id">
                          <span className="queue-id-cell">
                            {data?.id ? (
                              <Tooltip title={copiedEncounterId === data.id ? "Copied!" : data.id}>
                                <span
                                  className="queue-copy-id"
                                  onClick={(e) => handleCopyEncounterId(data.id, e)}
                                >
                                  <span className="queue-copy-id-text">{data.id}</span>
                                  <ContentCopyOutlined className="queue-copy-id-icon" />
                                </span>
                              </Tooltip>
                            ) : (
                              "-"
                            )}
                            {data?.intakeQuestions?.preferredChannel === 'phone' || data?.intakeQuestions?.preferredChannel === 'video/chat' ? (
                              <Tooltip title={data?.intakeQuestions?.preferredChannel === 'phone' ? "Audio" : "Video"}>
                                {data?.intakeQuestions?.preferredChannel === 'phone' ? (
                                  <span className="queue-channel-icon queue-channel-phone"><CallOutlined /></span>
                                ) : (
                                  <span className="queue-channel-icon queue-channel-video"><VideocamOutlined /></span>
                                )}
                              </Tooltip>
                            ) : (
                              <span className="queue-channel-icon queue-channel-empty" aria-hidden="true" />
                            )}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {data.orgName}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {data?.patientLocation}
                        </td>
                        {/* <td style={{ textAlign: "center" }}>
                          {data?.intakeQuestions?.intakeReason || data?.intakeQuestions?.reason ? (
                            <Tooltip
                              title={<>{data?.intakeQuestions?.intakeReason || data?.intakeQuestions?.reason}</>}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Link variant="primary" className="view-btn">
                                  <InfoOutlined style={{ color: "#005ba8" }} />
                                </Link>
                              </div>
                            </Tooltip>
                          ) : (
                            " "
                          )}
                        </td> */}
                        <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              {(() => {
                                const reasonText = data?.intakeQuestions?.intakeReason || data?.intakeQuestions?.reason || "-";
                                return (
                                  <Tooltip title={reasonText !== "-" ? reasonText : ""}>
                                    <span className="queue-reason-text">{reasonText}</span>
                                  </Tooltip>
                                );
                              })()}
                              <Tooltip title="Edit reason for call">
                                <Link
                                  variant="primary"
                                  className="view-btn queue-icon-link"
                                  onClick={() => {
                                    setEditReasonModalData(data);
                                    setEditReasonModalShow(true);
                                  }}
                                  style={{ flexShrink: 0 }}
                                >
                                  <EditOutlined />
                                </Link>
                              </Tooltip>
                            </div>
                         </td>
                        {selectedStatus?.value !== "finished" && (
                          <td style={{ textAlign: "left" }}>
                            <span className={`queue-wait-badge ${waitBandClass(waitMinutes)}`}>{waitTime}</span>
                          </td>
                        )}
                        {selectedStatus?.value === "finished" && (
                          <td style={{ textAlign: "center" }}>
                            {ViewDate(data?.createdAt)}
                          </td>
                        )}
                        <td style={{ textAlign: "center", verticalAlign: "middle", minWidth: "56px" }}>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {data?.notes?.length > 0 ? (
                              <Tooltip
                                title={
                                  <>
                                    Note:{" "}
                                    {data.notes[data.notes.length - 1]?.note}
                                    <br />
                                    <br /> {/* Empty line */}
                                    Added by:{" "}
                                    {capitalizeWords(
                                      data.notes[data.notes.length - 1]?.actor
                                        ?.display
                                    )}
                                    <br />
                                    Timestamp:{" "}
                                    {moment(
                                      data.notes[data.notes.length - 1]?.updatedAt
                                    ).format("DD-MMM-YY HH:mm")}
                                  </>
                                }
                              >
                                <div
                                  className="action-wrap"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Link
                                    onClick={() => handleViewNotesClick(data)}
                                    style={{
                                      backgroundColor: "#fff",
                                      border: "1px solid #cee0ef",
                                      borderRadius: "50%",
                                      padding: "5px",
                                    }}
                                  >
                                    <DescriptionOutlined
                                      style={{
                                        color: "#005da8",
                                        width: "24px",
                                        height: "20px",
                                      }}
                                    />
                                  </Link>
                                </div>
                              </Tooltip>
                            ) : (
                              " "
                            )}
                          </div>
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {data?.practitionerHistory && data?.practitionerHistory?.[
                              data?.practitionerHistory?.length - 1
                            ]?.actor?.display &&
                            data?.practitionerHistory?.[
                              data?.practitionerHistory?.length - 1
                            ]?.actor?.display !== "-" ? (
                              <div>
                                <span>
                                  {capitalizeWords(
                                    data?.practitionerHistory[
                                      data?.practitionerHistory.length - 1
                                    ]?.actor?.display
                                  )}
                                </span>
                              </div>
                            ) : (
                              <div>
                                <Button
                                  title="Assign Practitioner"
                                  className="queue-btn-compact"
                                  onClick={() =>
                                    handleAssignPractitionerClick(data)
                                  }
                                >
                                  Assign
                                </Button>
                              </div>
                            )}
                            {data?.practitionerHistory?.length > 0 && (
                              <Tooltip
                                title={"View Assigned Practitioner History"}
                                style={{ marginLeft: "5px" }}
                              >
                                <div>
                                  <Link
                                    variant="primary"
                                    className="view-btn queue-icon-link"
                                    onClick={() =>
                                      handleAssignPractitionerClick(data)
                                    }
                                  >
                                    <History style={{ color: "#005ba8" }} />
                                  </Link>
                                </div>
                              </Tooltip>
                            )}
                          </div>
                        </td>

                        <td>
                          <div
                            className="action-wrap"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {data?.status !== "finished" && (
                              <Button
                                title="View Details And Action Items"
                                className="queue-btn-compact"
                                onClick={() => handleViewEncounterClick(data)}
                              >
                                View
                              </Button>
                            )}
                            {data?.status === "finished" && (
                              <div
                                onClick={() =>
                                  handlePreviewEncounterClick(data, "final")
                                }
                              >
                                <Tooltip title="View Encounter Report">
                                  <Link to="" className="success-btn">
                                    <DescriptionOutlined
                                      style={{
                                        color: "white",
                                        fontSize: "large",
                                      }}
                                    />
                                  </Link>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <TableCell colSpan={"8"} style={{ textAlign: "center" }}>
                    No Data Available
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
          </div>
        </div>
        {searchFilterData?.length > 0 ? (
          <div style={{ marginTop: "16px" }}>
          <CustomPagination
            tableData={searchFilterData}
            totalRows={searchFilterData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            handlePage={(pageNo) => setPage(pageNo)}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
          />
          </div>
        ) : null}
      </section>
      {addNoteModalShow && (
        <AddNoteModal
          data={addNoteModalData}
          onShow={addNoteModalShow}
          onHide={() => {
            setAddNoteModalShow(false)
            if (selectedStatus?.value === "follow-up" || selectedStatus?.value === "parked") {
              fetchData(userScopeOfPractice);
            }
          }}
        />
      )}

      {viewNoteModalShow && (
        <ViewNotesModal
          recordData={addNoteModalData}
          onShow={viewNoteModalShow}
          onHide={() => {
            setViewNoteModalShow(false)
            if (selectedStatus?.value === "follow-up" || selectedStatus?.value === "parked") {
              fetchData(userScopeOfPractice);
            }
            
          }}
        />
      )}
      {assignPractModalShow && (
        <AssignPractitionerModal
          data={assignPractData}
          onShow={assignPractModalShow}
          onHide={() => {
            setAssignPractModalShow(false)
            if (selectedStatus?.value === "follow-up" || selectedStatus?.value === "parked") {
              fetchData(userScopeOfPractice);
            }
          }}
          onRefresh={() => fetchData(userScopeOfPractice)}
        />
      )}
      {priorityHistoryModal && (
        <PriorityHistoryModal
          data={selectedRecord}
          onShow={priorityHistoryModal}
          onHide={() => {
            setPriorityHistoryModal(false)
            if (selectedStatus?.value === "follow-up" || selectedStatus?.value === "parked") {
              fetchData(userScopeOfPractice);
            }
          }}
        />
      )}
      {encounterModal && (
        <ViewEncounterModal
          data={searchFilterData.filter(res => res.id ===selectedRecord.id)[0]  || selectedRecord}
          onShow={encounterModal}
          isCTIEnabled={isCTIEnabled}
          organizations={organizations}
          onHide={() => {
            setEncounterModal(false)
            if (selectedStatus?.value === "follow-up" || selectedStatus?.value === "parked") {
              fetchData(userScopeOfPractice);
            }
          }}
        />
      )}
      {editReasonModalShow && editReasonModalData && (
        <EditReasonForCallModal
          data={editReasonModalData}
          onShow={editReasonModalShow}
          onHide={() => {
            setEditReasonModalShow(false);
            setEditReasonModalData(null);
          }}
          onSuccess={() => {
            if (userScopeOfPractice) fetchData(userScopeOfPractice);
          }}
        />
      )}
    </>
  );
}

export default Queue;
